/**
 * Pure form logic for the profile "Muuda mustrit" slide-over (TASK-208).
 *
 * Flex assignments carry an explicit contracted weekly-hours figure (Humaans'
 * "hours contracted") — the day toggles only mark WHICH days are worked, so
 * the weekly total must not be derived from them (a 20h/week flex across five
 * days would otherwise read as 5 × standard day = 40h → FTE 1).
 */

export interface AssignFormState {
    mode: 'existing' | 'custom'
    customKind: 'fixed' | 'flex'
    patternId: number
    name: string
    /** Mon-first; fixed: hours per day, flex: >0 marks a working day. */
    days: number[]
    /** Flex only: contracted hours per week. */
    weeklyHours: number
    fullTimeHours: number
    standardDayHours: number
    effectiveFrom: string
}

export interface AssignRequestBody {
    working_pattern_id?: number
    effective_from: string
    custom?: {
        mode: 'fixed' | 'flex'
        working_pattern: (number | boolean)[]
        name?: string
        full_time_hours?: number
        standard_day_hours?: number
        weekly_hours?: number
    }
}

const sumDays = (days: number[]): number =>
    days.reduce((sum, d) => sum + (Number.isFinite(d) ? d : 0), 0)

/** Weekly working hours the assignment represents (drives the FTE preview). */
export const deriveWeeklyHours = (s: AssignFormState): number =>
    s.mode === 'custom' && s.customKind === 'flex' ? s.weeklyHours : sumDays(s.days)

export const isAssignReady = (s: AssignFormState): boolean => {
    if (!s.effectiveFrom) return false
    if (s.mode === 'existing') return s.patternId > 0
    if (!s.days.every((d) => d >= 0 && d <= 24)) return false
    if (s.customKind === 'flex') {
        return s.weeklyHours > 0 && s.weeklyHours <= 80 && s.days.some((d) => d > 0)
    }
    return sumDays(s.days) > 0
}

export const buildAssignRequest = (s: AssignFormState): AssignRequestBody => {
    if (s.mode === 'existing') {
        return { working_pattern_id: s.patternId, effective_from: s.effectiveFrom }
    }
    return {
        effective_from: s.effectiveFrom,
        custom: {
            mode: s.customKind,
            working_pattern: s.customKind === 'flex' ? s.days.map((d) => d > 0) : s.days,
            name: s.name.trim() || undefined,
            full_time_hours: s.fullTimeHours,
            standard_day_hours: s.standardDayHours,
            ...(s.customKind === 'flex' ? { weekly_hours: s.weeklyHours } : {}),
        },
    }
}
